import {
  ArrowsInLineHorizontal,
  Broadcast,
  Globe,
  SlidersHorizontal,
  Tag,
  type Icon,
} from "@phosphor-icons/react"

import { FRAMING_SIGNAL_THEME } from "~/utils/demandFramingSignal"
import { formatName } from "~/utils/namingUtils"
import type { AdjustmentBand } from "~/utils/demandProjection"

// Typed presets over DemandAdjustment: each compiles to a plain adjustment with its id in `theme`.
type SignalInputMode = "units" | "percentage" | "either"
export type SignalCategory = "Strategy" | "Misc"
type SignalSign = "uplift" | "reduction"
type SignalScopeHint = "warehouse" | "channel" | "market" | "all"

export interface SignalType {
  id: string // -> DemandAdjustment.theme
  label: string
  icon: Icon
  category: SignalCategory
  stage: "setup" | "overlay"
  inputMode: SignalInputMode // "either" lets the planner choose units vs %
  sign: SignalSign // "reduction" => the entered magnitude is persisted negative
  scopeHint: SignalScopeHint // which axis the planner most likely scopes to
  windowed: boolean // overlay signals default to a date window
  defaultTarget?: AdjustmentBand // which band an on-top signal pre-targets (default "sales")
  description: string
  whyTemplate: string // explanatory copy for the dialog + traceability
  // New volume has no rows on that axis yet, so scoping to one would match nothing.
  lockedAxis?: "marketId" | "channel"
  // Replaces the form's "Value" label where a bare value would be misread.
  valueLabel?: string
  // Engine-synthesized, never authored: resolvable for its badge, absent from the picker.
  derived?: boolean
  // What the signal asks for beyond name and value; keys match its `SIGNAL_INPUT_SCHEMAS` params.
  paramFields?: { key: string; label: string; placeholder: string }[]
  // Example input, shown as placeholders so each field says what belongs in it.
  examples?: { name: string; description: string }
}

export const CUSTOM_SIGNAL_ID = "custom"

export const SIGNAL_TYPES: SignalType[] = [
  {
    id: FRAMING_SIGNAL_THEME,
    label: "Cannibalization",
    icon: ArrowsInLineHorizontal,
    category: "Strategy",
    stage: "setup",
    inputMode: "percentage",
    sign: "uplift",
    scopeHint: "all",
    windowed: false,
    derived: true,
    description:
      "How much of an assortment change existing variants absorb, from the Strategy answer under Assortment.",
    whyTemplate: "Brings each side of the plan to the units that answer simulates for it.",
  },
  {
    id: "price-change",
    label: "Price Change",
    icon: Tag,
    category: "Strategy",
    // A windowless overlay row folds at the leaf stage, so it needs no band target.
    stage: "overlay",
    inputMode: "either",
    sign: "uplift",
    scopeHint: "all",
    windowed: false,
    valueLabel: "Demand change",
    description: "A change in demand from a price move, in % or units",
    whyTemplate:
      "The value is the demand change itself. No price elasticity is applied, so −10% means 10% less demand, not a 10% price cut.",
    examples: {
      name: "e.g. Spring price cut",
      description: "e.g. 10% lower prices on core styles",
    },
  },
  {
    id: "new-channel",
    label: "New Channel",
    icon: Broadcast,
    category: "Strategy",
    stage: "setup",
    inputMode: "units",
    sign: "uplift",
    scopeHint: "channel",
    windowed: false,
    lockedAxis: "channel",
    description: "Demand from entering a channel the plan didn't sell in before.",
    whyTemplate: "Net-new volume from a new channel.",
    paramFields: [
      { key: "channelName", label: "Channel being entered", placeholder: "e.g. Zalando" },
    ],
    examples: {
      name: "e.g. Zalando launch",
      description: "e.g. Selling the full range on Zalando from March",
    },
  },
  {
    id: "new-market",
    label: "New Market",
    icon: Globe,
    category: "Strategy",
    stage: "setup",
    inputMode: "units",
    sign: "uplift",
    scopeHint: "market",
    windowed: false,
    lockedAxis: "marketId",
    description: "Demand from entering a market the plan didn't sell in before.",
    whyTemplate: "Net-new volume from a new market.",
    paramFields: [
      { key: "marketName", label: "Market being entered", placeholder: "e.g. Germany" },
    ],
    examples: {
      name: "e.g. Germany launch",
      description: "e.g. Opening webshop delivery to Germany",
    },
  },
  {
    id: CUSTOM_SIGNAL_ID,
    label: "Custom",
    icon: SlidersHorizontal,
    category: "Misc",
    stage: "overlay",
    inputMode: "either",
    sign: "uplift",
    scopeHint: "all",
    windowed: false,
    valueLabel: "Demand change",
    description: "Set the workflow, value type and scope yourself.",
    whyTemplate: "",
    examples: {
      name: "e.g. New retail store",
      description: "e.g. Opening a new Berlin store",
    },
  },
]

const BY_ID = new Map(SIGNAL_TYPES.map((s) => [s.id, s]))
const CUSTOM = BY_ID.get(CUSTOM_SIGNAL_ID)!

export function signalTypeForTheme(theme?: string | null): SignalType {
  return (theme && BY_ID.get(theme)) || CUSTOM
}

// Where a signal normally goes, not where the form will land it.
const SCOPE_CHIP: Record<SignalScopeHint, string> = {
  warehouse: "Warehouse-level",
  channel: "Channel-level",
  market: "Market-level",
  all: "Any level",
}

// Reorders + Pre-orders = Total demand: the "Applies to" picker, badges and chips name a band so.
export const adjustmentBandLabel = (band: AdjustmentBand): string => {
  if (band === "preorder") return formatName("preorder", true, true)
  return band === "both" ? "Total demand" : "Reorders"
}

// The chips on picker tiles and the "what this does" panel.
export function signalMetaChips(s: SignalType): string[] {
  const chips: string[] = [s.stage === "setup" ? "Demand signal" : "Campaign"]
  chips.push(s.inputMode === "percentage" ? "%" : s.inputMode === "units" ? "Units" : "% or units")
  chips.push(SCOPE_CHIP[s.scopeHint])
  if (s.sign === "reduction") chips.push("Removes")
  if (s.windowed) chips.push("Windowed")
  if (s.defaultTarget === "preorder" || s.defaultTarget === "both")
    chips.push(adjustmentBandLabel(s.defaultTarget))
  return chips
}
